import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { PageState } from '../app/PublicLayout.jsx'
import { assetUrl, useCollection, useSite } from '../app/useSiteData.js'
import { useI18n } from '../lib/i18n.jsx'
import { useTheme } from '../lib/theme.jsx'
import {
  Container,
  EmptyState,
  Grid,
  Overline,
  Row,
  RowList,
  Section,
  SectionHead,
} from '../ui/index.js'
import { collectMemberNames } from './Members.jsx'
import { NewsRow } from './NewsList.jsx'
import { PublicationRow } from './Publications.jsx'
import { ResearchCard } from './ResearchList.jsx'

/**
 * Home page.
 *
 * A type-only hero — eyebrow, group name at display size, one sentence, two
 * buttons — occupying roughly half the viewport so the first band of real
 * content is already peeking above the fold. A full-height hero with a scroll
 * chevron would say "experience this site"; a research group's homepage should
 * say "here is what we did last month".
 *
 * Everything below is a window onto a page that owns it, rendered by that
 * page's own components: news rows come from NewsList, direction cards from
 * ResearchList, publication rows from Publications. The home page therefore
 * cannot drift from the pages it summarises — there is no second, prettier
 * rendering of a paper that disagrees with the real list.
 *
 * 首页。
 *
 * 纯文字的主视觉——眉标、展示级字号的课题组名、一句话、两个按钮——大约占半屏，
 * 让第一段真实内容在首屏底部已经露头。占满整屏再配一个向下箭头的主视觉说的是
 * 「来体验这个网站」；课题组主页该说的是「这是我们上个月做的事」。
 *
 * 下面的每一段都是某个页面的窗口，并由那个页面自己的组件渲染：新闻行来自 NewsList、
 * 方向卡片来自 ResearchList、论文行来自 Publications。因此首页不可能和它所概括的
 * 页面走样——不存在「另一个更好看、却和真正列表对不上」的论文渲染。
 */

/** How many news items and papers the home page shows. 首页展示的新闻与论文条数。 */
const HOME_LIMIT = 3

/** How many research directions fill the 2-up band. 填满 2 列区块的研究方向数。 */
const HOME_RESEARCH_LIMIT = 4

/**
 * Home page.
 *
 * 首页。
 *
 * @returns {JSX.Element} Page / 页面。
 */
export default function Home() {
  const { t, pick } = useI18n()
  const site = useSite()
  const news = useCollection('news')
  const research = useCollection('research')
  const publications = useCollection('publications')
  const members = useCollection('members')

  const settings = site.data
  const memberNames = useMemo(() => collectMemberNames(members.data), [members.data])

  const latestNews = useMemo(
    () =>
      [...(news.data || [])]
        .sort((a, b) => {
          if (Boolean(a.pinned) !== Boolean(b.pinned)) return a.pinned ? -1 : 1
          return String(b.date || '').localeCompare(String(a.date || ''))
        })
        .slice(0, HOME_LIMIT),
    [news.data]
  )

  // "Selected" means the group marked it so. Only when nobody has chosen does
  // the site fall back to recency — a homepage should show the work the group
  // is proud of, not merely the work it uploaded last.
  // 「代表性」意味着课题组自己标了。只有在谁都没标时才退回按时间取——首页该展示课题组
  // 引以为傲的工作，而不是最后上传的那几篇。
  const selectedPublications = useMemo(() => {
    const all = publications.data || []
    const featured = all.filter((record) => record.featured === true)
    const pool = featured.length > 0 ? featured : all
    return [...pool].sort((a, b) => Number(b.year || 0) - Number(a.year || 0)).slice(0, HOME_LIMIT)
  }, [publications.data])

  const joinNote = settings ? pick(settings.joinNote) : ''
  const heroLayout = settings?.heroLayout === 'band' ? 'band' : 'side'

  // One picture per background, chosen here rather than by CSS, so only the one that
  // will be seen is fetched. `isDark` is already resolved — it follows the explicit
  // toggle and, on 'system', tracks the OS preference live — so the swap happens when
  // the reader changes theme, not only on reload.
  //
  // Rendering both and hiding one with a media query would work with the system
  // preference and not with the toggle, and would download close to a megabyte to show
  // half of it.
  //
  // 每种背景一张图，选择放在这里而不是交给 CSS，这样只会取回真正会被看到的那一张。
  // `isDark` 已经是解析好的结果——它跟随显式切换，在「跟随系统」时还实时跟踪系统偏好
  // ——因此读者切换主题时图会跟着换，而不是只在刷新时才换。
  //
  // 把两张都渲染出来、用媒体查询藏掉一张，对系统偏好有效、对手动切换无效，
  // 而且会为了显示其中一半而下载将近一兆。
  const { isDark } = useTheme()
  const heroSrc = (isDark && settings?.heroImageDark) || settings?.heroImage

  return (
    <>
      {/* The arrangement is a setting, so the class comes from data. Anything
          other than the two declared values falls back to `side` rather than
          rendering an unstyled hero: a settings document written before this
          field existed has no value at all, and every deployment has one of
          those until someone saves the form.
          排布是一项设置，因此这个 class 来自数据。除两个声明值以外的任何取值都回退到
          `side`，而不是渲染一个没有样式的主视觉：在这个字段存在之前写下的设置文档
          根本没有这个值，而每一个部署在有人保存表单之前都是那种状态。 */}
      <Section className={`home-hero home-hero--${heroLayout}`} tone="default">
        <Container width="max">
          <Overline className="home-hero__eyebrow">{t('home.eyebrow')}</Overline>
          {/* The group's name is data, so the hero waits for it rather than
              painting an empty <h1> and reflowing when the settings land.
              课题组名称是数据，因此主视觉等它到达，而不是先画一个空 <h1>、
              等设置回来再重排。 */}
          <PageState loading={site.loading} error={site.error} onRetry={site.reload} lines={3}>
            <h1 className="home-hero__title">{settings ? pick(settings.groupName) : ''}</h1>
            <p className="home-hero__lede">{settings ? pick(settings.tagline) || pick(settings.intro) : ''}</p>
            <Row className="home-hero__actions" gap="base">
              <Link className="link-button link-button--primary" to="/research">
                {t('home.ctaResearch')}
              </Link>
              <Link className="link-button link-button--ghost" to="/publications">
                {t('home.ctaPublications')}
              </Link>
            </Row>
            {/* The hero image is optional and decorative, so it renders only
                once one is set and carries an empty alt: a screen reader that
                announced it would be reading out decoration between the
                tagline and the first real section. Before this, `heroImage`
                existed in the schema and in the console but nothing consumed
                it, so an uploaded image went nowhere.
                主图是可选的装饰元素：只有设置了才渲染，且 alt 为空——读屏软件若
                播报它，等于在标语和第一段正文之间念一段装饰。在此之前
                `heroImage` 只存在于 schema 和后台，没有任何组件消费它，传上去的
                图因此无处显示。 */}
            {heroSrc ? (
              <figure className="home-hero__figure">
                <img className="home-hero__image" src={assetUrl(heroSrc)} alt="" />
              </figure>
            ) : null}
          </PageState>
        </Container>
      </Section>

      <Section className="home-news" tone="subtle" divided>
        <Container width="narrow">
          <SectionHead
            title={t('home.latestNews')}
            aside={
              <Link className="section-link" to="/news">
                {t('common.viewAll')}
              </Link>
            }
          />
          <PageState
            loading={news.loading}
            error={news.error}
            onRetry={news.reload}
            isEmpty={latestNews.length === 0}
            empty={<EmptyState title={t('news.empty')} body={t('news.emptyBody')} />}
            lines={3}
          >
            <RowList className="news-list news-list--home">
              {latestNews.map((record) => (
                <NewsRow key={record.id} record={record} />
              ))}
            </RowList>
          </PageState>
        </Container>
      </Section>

      <Section className="home-research" divided>
        <Container width="max">
          <SectionHead
            title={t('home.researchAreas')}
            aside={
              <Link className="section-link" to="/research">
                {t('common.viewAll')}
              </Link>
            }
          />
          <PageState
            loading={research.loading}
            error={research.error}
            onRetry={research.reload}
            isEmpty={(research.data || []).length === 0}
            empty={<EmptyState title={t('research.empty')} body={t('research.emptyBody')} />}
            lines={3}
          >
            <Grid columns={2} className="research-grid research-grid--home">
              {(research.data || []).slice(0, HOME_RESEARCH_LIMIT).map((record) => (
                <ResearchCard key={record.id} record={record} />
              ))}
            </Grid>
          </PageState>
        </Container>
      </Section>

      <Section className="home-publications" tone="subtle" divided>
        <Container width="narrow">
          <SectionHead
            title={t('home.selectedPublications')}
            aside={
              <Link className="section-link" to="/publications">
                {t('common.viewAll')}
              </Link>
            }
          />
          <PageState
            loading={publications.loading}
            error={publications.error}
            onRetry={publications.reload}
            isEmpty={selectedPublications.length === 0}
            empty={<EmptyState title={t('publications.empty')} body={t('publications.emptyBody')} />}
            lines={3}
          >
            <RowList className="pub-list pub-list--home">
              {selectedPublications.map((record) => (
                <PublicationRow key={record.id} record={record} memberNames={memberNames} />
              ))}
            </RowList>
          </PageState>
        </Container>
      </Section>

      {/* Rendered only when the group wrote a note. An empty "join us" band with
          a button and no sentence is the fake density the design spec bans.
          只有课题组写了招生说明才渲染。一个只有按钮没有正文的「加入我们」区块，
          正是设计规范禁止的伪密度。 */}
      {joinNote && (
        <Section className="home-join" divided>
          <Container width="narrow">
            <SectionHead title={t('home.joinUs')} />
            <p className="home-join__body">{joinNote}</p>
            <Link className="link-button link-button--ghost" to="/contact">
              {t('nav.contact')}
            </Link>
          </Container>
        </Section>
      )}
    </>
  )
}
